/**
 * Deploy — nasazení infrastruktury: PXE/iPXE, ISO, preseed, ACM Live průvodce.
 * (dříve NetBoot; součásti netboot + live pod jedním modulem)
 */
import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { apiProxyUrl } from '../../lib/service-endpoints';
import { DEFAULT_API_TLS_PORT } from '../../lib/node-ports';
import { PageHeader } from '../../components/ui/PageHeader';
import { StatCard, StatsGrid } from '../../components/ui/Stats';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Tab, TabList, TabPanel, Tabs } from '../../components/ui/Tabs';
import { SearchInput } from '../../components/ui/SearchInput';
import { Select } from '../../components/ui/Select';
import {
  AlertCircle,
  CheckCircle2,
  Copy,
  Disc,
  Download,
  Eye,
  FileText,
  HardDriveDownload,
  Loader2,
  Play,
  Plus,
  Power,
  RefreshCw,
  Server,
  Settings,
  Shield,
  Square,
  Trash2,
  Upload,
  Wrench,
  XCircle,
} from 'lucide-react';
import { themedConfirm, themedPrompt, confirmThen } from '../../lib/themed-dialogs';
import LiveDeployWizard from './live-deploy';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Iso {
  id: string;
  filename: string;
  label: string;
  size_bytes: number;
  sha256: string;
  distro: string;
  version: string;
  arch: string;
  extracted: boolean;
  uploaded_at: string;
}

interface Preseed {
  id: string;
  name: string;
  label: string;
  distro: string;
  content?: string;
  variables: Record<string, string>;
  is_template: boolean;
  created_at: string;
  updated_at: string;
}

interface Build {
  id: string;
  name: string;
  type: string;
  status: string;
  config: Record<string, string>;
  iso_label: string;
  preseed_label: string;
  created_at: string;
  built_at: string;
}

interface NetbootStatus {
  ok: boolean;
  dnsmasq: string;
  container: string;
  interfaces: string[];
  server_ip: string;
  api_port?: number;
  isos: number;
  preseeds: number;
  builds: number;
  builds_ready: number;
}

interface NetworkInterface {
  name: string;
  addresses: string[];
  up: boolean;
}

interface NetbootConfig {
  interfaces: string[];
  server_ip: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b / 1024).toFixed(0)} KB`;
  if (b < 1073741824) return `${(b / 1048576).toFixed(1)} MB`;
  return `${(b / 1073741824).toFixed(2)} GB`;
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    ready: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
    running: 'text-blue-400 bg-blue-500/10 border-blue-500/20',
    pending: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
    building: 'text-blue-400 bg-blue-500/10 border-blue-500/20',
    error: 'text-red-400 bg-red-500/10 border-red-500/20',
    stopped: 'text-gray-400 bg-gray-500/10 border-gray-500/20',
  };
  return (
    <span
      className={`px-2 py-0.5 rounded-lg text-[10px] font-medium border ${
        colors[status] ?? colors.stopped
      }`}
    >
      {status}
    </span>
  );
}

// ─── ISO Tab ──────────────────────────────────────────────────────────────────

function IsoTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  // Multipart ISO upload remains capability (raw fetch FormData) — list/delete via MCP
  const { data, isLoading } = useQuery({
    queryKey: ['netboot-isos'],
    queryFn: () => api.mcp<{ ok: boolean; isos: Iso[] }>('netboot.iso_list', {}),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.mcp('netboot.iso_delete', { id }),
    onSuccess: () => {
      toast.success(t('netboot.isoDeleted'));
      qc.invalidateQueries({ queryKey: ['netboot-isos'] });
    },
  });

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('label', file.name.replace(/\.iso$/i, ''));
      form.append('distro', 'debian');
      // Multipart binary upload — presentation capability exception (not MCP R/R)
      await fetch(apiProxyUrl('/apiext/netboot/iso'), {
        method: 'POST',
        credentials: 'include',
        body: form,
      });
      toast.success(t('netboot.isoUploaded'));
      qc.invalidateQueries({ queryKey: ['netboot-isos'] });
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const isos = data?.isos ?? [];

  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-between'>
        <p className='text-xs text-gray-500'>{t('netboot.isoCount', { count: isos.length })}</p>
        <label className='flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium cursor-pointer transition-colors'>
          {uploading ? <Loader2 size={14} className='animate-spin' /> : <Upload size={14} />}
          {uploading ? t('netboot.uploading') : t('netboot.uploadIso')}
          <input
            ref={fileRef}
            type='file'
            accept='.iso'
            className='hidden'
            onChange={handleUpload}
            disabled={uploading}
          />
        </label>
      </div>

      {isLoading
        ? <div className='text-center py-8 text-gray-600 text-sm'>{t('common.loading')}</div>
        : isos.length === 0
        ? (
          <div className='text-center py-16 text-gray-500'>
            <Disc size={36} className='mx-auto mb-3 text-gray-700' />
            <p>{t('netboot.noIsos')}</p>
            <p className='text-xs mt-1 text-gray-600'>{t('netboot.uploadIsoHint')}</p>
          </div>
        )
        : (
          <div className='rounded-xl border border-gray-800 overflow-hidden bg-gray-900/20'>
            <table className='w-full text-left text-sm'>
              <thead className='bg-gray-900/40 border-b border-gray-800'>
                <tr>
                  <th className='px-4 py-3 text-xs font-semibold text-gray-500 uppercase'>
                    {t('netboot.labelField')}
                  </th>
                  <th className='px-4 py-3 text-xs font-semibold text-gray-500 uppercase'>
                    {t('netboot.colDistro')}
                  </th>
                  <th className='px-4 py-3 text-xs font-semibold text-gray-500 uppercase'>
                    {t('netboot.colSize')}
                  </th>
                  <th className='px-4 py-3 text-xs font-semibold text-gray-500 uppercase'>
                    {t('netboot.colKernel')}
                  </th>
                  <th className='px-4 py-3 text-xs font-semibold text-gray-500 uppercase'>
                    {t('netboot.colActions')}
                  </th>
                </tr>
              </thead>
              <tbody className='divide-y divide-gray-800'>
                {isos.map((iso) => (
                  <tr key={iso.id} className='hover:bg-gray-800/30'>
                    <td className='px-4 py-3'>
                      <div className='font-medium text-white text-xs'>{iso.label}</div>
                      <div className='text-[10px] text-gray-600 font-mono'>{iso.filename}</div>
                    </td>
                    <td className='px-4 py-3 text-gray-400 text-xs'>
                      {iso.distro} {iso.version} ({iso.arch})
                    </td>
                    <td className='px-4 py-3 text-gray-400 text-xs'>
                      {formatBytes(iso.size_bytes)}
                    </td>
                    <td className='px-4 py-3'>
                      {iso.extracted
                        ? <CheckCircle2 size={14} className='text-emerald-400' />
                        : <XCircle size={14} className='text-red-400' />}
                    </td>
                    <td className='px-4 py-3'>
                      <button
                        onClick={async () => {
                          if (await themedConfirm(t('netboot.confirmDeleteIso'))) deleteMut.mutate(iso.id);
                        }}
                        className='p-1.5 rounded-lg text-gray-600 hover:text-red-400 hover:bg-gray-800'
                      >
                        <Trash2 size={13} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
}

// ─── Create Build Modal ──────────────────────────────────────────────────────

function CreateBuildModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [isoId, setIsoId] = useState('');
  const [preseedId, setPreseedId] = useState('');
  const [hostname, setHostname] = useState('acm-node');
  const [ip, setIp] = useState('dhcp');
  const [gateway, setGateway] = useState('');
  const [rootPw, setRootPw] = useState('123456Aa');

  const { data: isos } = useQuery({
    queryKey: ['netboot-isos'],
    queryFn: () => api.mcp<{ ok: boolean; isos: Iso[] }>('netboot.iso_list', {}),
  });
  const { data: preseeds } = useQuery({
    queryKey: ['netboot-preseeds'],
    queryFn: () => api.mcp<{ ok: boolean; preseeds: Preseed[] }>('netboot.preseeds', { action: 'list' }),
  });

  const createMut = useMutation({
    mutationFn: () =>
      api.mcp('netboot_create_build', {
        name,
        iso_id: isoId,
        preseed_id: preseedId,
        type: 'pxe',
        config: { hostname, ip, gateway, root_password: rootPw },
      }),
    onSuccess: () => {
      toast.success(t('netboot.buildCreated'));
      onCreated();
      onClose();
    },
    onError: (e) => toast.error((e as Error).message),
  });

  // Auto-select first ISO/preseed
  if (!isoId && isos?.isos?.length) setIsoId(isos.isos[0].id);
  if (!preseedId && preseeds?.preseeds?.length) setPreseedId(preseeds.preseeds[0].id);

  return (
    <div
      className='fixed inset-0 z-50 flex items-center justify-center bg-black/60'
      onClick={onClose}
    >
      <div
        className='bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-lg p-6 space-y-4'
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className='text-lg font-bold text-white'>{t('netboot.newPxeBuild')}</h3>

        <div className='space-y-3'>
          <div>
            <label className='text-xs text-gray-500 block mb-1'>{t('netboot.slugNoSpaces')}</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value.replace(/[^a-z0-9-]/g, ''))}
              placeholder='acm-os-custom'
              className='w-full bg-gray-800 border border-gray-700 rounded-xl px-3 py-2 text-sm font-mono text-white'
            />
          </div>

          <div className='grid grid-cols-2 gap-3'>
            <div>
              <label className='text-xs text-gray-500 block mb-1'>{t('netboot.isoNetboot')}</label>
              <Select
                className='w-full'
                value={isoId}
                onChange={(e) => setIsoId(e.target.value)}
              >
                {(isos?.isos ?? []).map((i) => <option key={i.id} value={i.id}>{i.label}</option>)}
              </Select>
            </div>
            <div>
              <label className='text-xs text-gray-500 block mb-1'>{t('netboot.preseedTemplate')}</label>
              <Select
                className='w-full'
                value={preseedId}
                onChange={(e) => setPreseedId(e.target.value)}
              >
                {(preseeds?.preseeds ?? []).map((p) => (
                  <option key={p.id} value={p.id}>{p.label}</option>
                ))}
              </Select>
            </div>
          </div>

          <div className='grid grid-cols-2 gap-3'>
            <div>
              <label className='text-xs text-gray-500 block mb-1'>{t('netboot.hostname')}</label>
              <input
                value={hostname}
                onChange={(e) => setHostname(e.target.value)}
                className='w-full bg-gray-800 border border-gray-700 rounded-xl px-3 py-2 text-sm font-mono text-white'
              />
            </div>
            <div>
              <label className='text-xs text-gray-500 block mb-1'>{t('netboot.ipOrDhcp')}</label>
              <input
                value={ip}
                onChange={(e) => setIp(e.target.value)}
                className='w-full bg-gray-800 border border-gray-700 rounded-xl px-3 py-2 text-sm font-mono text-white'
              />
            </div>
          </div>

          {ip !== 'dhcp' && (
            <div>
              <label className='text-xs text-gray-500 block mb-1'>{t('netboot.gateway')}</label>
              <input
                value={gateway}
                onChange={(e) => setGateway(e.target.value)}
                placeholder='10.119.251.1'
                className='w-full bg-gray-800 border border-gray-700 rounded-xl px-3 py-2 text-sm font-mono text-white'
              />
            </div>
          )}

          <div>
            <label className='text-xs text-gray-500 block mb-1'>{t('netboot.rootPassword')}</label>
            <input
              value={rootPw}
              onChange={(e) => setRootPw(e.target.value)}
              className='w-full bg-gray-800 border border-gray-700 rounded-xl px-3 py-2 text-sm font-mono text-white'
            />
          </div>
        </div>

        <div className='flex justify-end gap-2 pt-2'>
          <button
            onClick={onClose}
            className='px-4 py-2 rounded-xl text-sm text-gray-400 hover:text-white border border-gray-700 hover:bg-gray-800'
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={() => createMut.mutate()}
            disabled={!name || !isoId || !preseedId || createMut.isPending}
            className='flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-40'
          >
            {createMut.isPending
              ? <Loader2 size={14} className='animate-spin' />
              : <Plus size={14} />}
            {t('netboot.create')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Builds Tab ───────────────────────────────────────────────────────────────

function BuildsTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['netboot-builds'],
    queryFn: () =>
      api.mcp<{ ok: boolean; builds: Build[]; count: number }>(
        'netboot_list_builds',
        {},
        { silent: true },
      ),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.mcp('netboot.builds_delete', { id }),
    onSuccess: () => {
      toast.success(t('netboot.buildDeleted'));
      qc.invalidateQueries({ queryKey: ['netboot-builds'] });
    },
  });

  const builds = data?.builds ?? [];

  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-between'>
        <p className='text-xs text-gray-500'>{t('netboot.buildsCount', { count: builds.length })}</p>
        <button
          onClick={() => setShowCreate(true)}
          className='flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium'
        >
          <Plus size={14} /> {t('netboot.newBuild')}
        </button>
      </div>

      {showCreate && (
        <CreateBuildModal
          onClose={() => setShowCreate(false)}
          onCreated={() => qc.invalidateQueries({ queryKey: ['netboot-builds'] })}
        />
      )}

      {isLoading
        ? <div className='text-center py-8 text-gray-600 text-sm'>{t('common.loading')}</div>
        : builds.length === 0
        ? (
          <div className='text-center py-16 text-gray-500'>
            <Wrench size={36} className='mx-auto mb-3 text-gray-700' />
            <p>{t('netboot.noBuilds')}</p>
            <p className='text-xs mt-1 text-gray-600'>{t('netboot.noBuildsHint')}</p>
          </div>
        )
        : (
          <div className='space-y-2'>
            {builds.map((b) => (
              <Card key={b.id}>
                <CardContent className='py-3'>
                  <div className='flex items-center gap-3'>
                    <div className='flex-1 min-w-0'>
                      <div className='flex items-center gap-2'>
                        <span className='font-medium text-white text-sm'>{b.name}</span>
                        <StatusBadge status={b.status} />
                        <span className='text-[10px] text-gray-600 font-mono'>{b.type}</span>
                      </div>
                      <div className='text-[10px] text-gray-500 mt-0.5'>
                        {t('netboot.buildIsoPreseed', { iso: b.iso_label, preseed: b.preseed_label })}
                        {b.config?.hostname && ` · ${b.config.hostname}`}
                        {b.config?.ip && b.config.ip !== 'dhcp' && ` (${b.config.ip})`}
                      </div>
                    </div>
                    <button
                      onClick={async () => {
                        if (await themedConfirm(t('netboot.confirmDeleteBuild'))) deleteMut.mutate(b.id);
                      }}
                      className='p-1.5 rounded-lg text-gray-600 hover:text-red-400 hover:bg-gray-800'
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
    </div>
  );
}

// ─── Preseed Editor Modal ─────────────────────────────────────────────────────

function PreseedEditor({ preseed, onClose, onSaved }: {
  preseed: Preseed | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const isNew = !preseed?.id;
  const [name, setName] = useState(preseed?.name ?? '');
  const [label, setLabel] = useState(preseed?.label ?? '');
  const [content, setContent] = useState(preseed?.content ?? '');
  const [vars, setVars] = useState<Record<string, string>>(preseed?.variables ?? {});
  const [loading, setLoading] = useState(!isNew && !preseed?.content);
  const [newVarKey, setNewVarKey] = useState('');

  const { data: detail } = useQuery({
    queryKey: ['netboot-preseed-detail', preseed?.id],
    queryFn: () =>
      api.mcp<{ ok: boolean; preseed: Preseed }>('netboot.preseeds', {
        action: 'get',
        id: preseed!.id,
      }),
    enabled: !!preseed?.id && !preseed?.content,
  });
  if (detail?.preseed?.content && loading) {
    setContent(detail.preseed.content);
    setVars(detail.preseed.variables ?? {});
    setLoading(false);
  }

  const saveMut = useMutation({
    mutationFn: () =>
      api.mcp('netboot.preseeds', {
        action: 'save',
        id: preseed?.id,
        name,
        label,
        content,
        variables: vars,
      }),
    onSuccess: () => {
      toast.success(t('netboot.preseedSaved'));
      onSaved();
      onClose();
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const updateVar = (key: string, val: string) => setVars((prev) => ({ ...prev, [key]: val }));
  const removeVar = (key: string) =>
    setVars((prev) => {
      const n = { ...prev };
      delete n[key];
      return n;
    });
  const addVar = () => {
    if (newVarKey && !vars[newVarKey]) {
      setVars((prev) => ({ ...prev, [newVarKey]: '' }));
      setNewVarKey('');
    }
  };

  return (
    <div
      className='fixed inset-0 z-50 flex items-center justify-center bg-black/60'
      onClick={onClose}
    >
      <div
        className='bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col'
        onClick={(e) => e.stopPropagation()}
      >
        <div className='p-5 border-b border-gray-800 flex items-center justify-between'>
          <h3 className='text-lg font-bold text-white'>
            {isNew ? t('netboot.newPreseed') : t('netboot.editingLabel', { label })}
          </h3>
          <button onClick={onClose} className='text-gray-500 hover:text-white'>
            <XCircle size={18} />
          </button>
        </div>

        <div className='flex-1 overflow-auto p-5 space-y-4'>
          <div className='grid grid-cols-2 gap-3'>
            <div>
              <label className='text-xs text-gray-500 block mb-1'>{t('netboot.slug')}</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value.replace(/[^a-z0-9-]/g, ''))}
                disabled={!isNew}
                className='w-full bg-gray-800 border border-gray-700 rounded-xl px-3 py-2 text-sm font-mono text-white disabled:opacity-50'
              />
            </div>
            <div>
              <label className='text-xs text-gray-500 block mb-1'>{t('netboot.labelField')}</label>
              <input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                className='w-full bg-gray-800 border border-gray-700 rounded-xl px-3 py-2 text-sm text-white'
              />
            </div>
          </div>

          {/* Variables */}
          <div>
            <label className='text-xs text-gray-500 block mb-2'>
              {t('netboot.variablesLabel', { placeholder: '{{var}}' })}
            </label>
            <div className='space-y-1.5'>
              {Object.entries(vars).map(([k, v]) => (
                <div key={k} className='flex gap-2 items-center'>
                  <span className='text-xs text-gray-400 font-mono w-40 shrink-0 truncate'>
                    {`{{${k}}}`}
                  </span>
                  <input
                    value={v}
                    onChange={(e) =>
                      updateVar(k, e.target.value)}
                    className='flex-1 bg-gray-800 border border-gray-700 rounded-lg px-2 py-1.5 text-xs font-mono text-white'
                  />
                  <button
                    onClick={() =>
                      removeVar(k)}
                    className='text-gray-600 hover:text-red-400'
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
              <div className='flex gap-2 items-center mt-2'>
                <input
                  value={newVarKey}
                  onChange={(e) => setNewVarKey(e.target.value.replace(/[^a-z0-9_]/g, ''))}
                  placeholder={t('netboot.newVariablePlaceholder')}
                  onKeyDown={(e) => e.key === 'Enter' && addVar()}
                  className='w-40 bg-gray-800 border border-gray-700 rounded-lg px-2 py-1.5 text-xs font-mono text-white'
                />
                <button
                  onClick={addVar}
                  disabled={!newVarKey}
                  className='text-xs text-blue-400 hover:text-blue-300 disabled:opacity-30'
                >
                  {t('netboot.addVariable')}
                </button>
              </div>
            </div>
          </div>

          {/* Content editor */}
          <div>
            <label className='text-xs text-gray-500 block mb-1'>{t('netboot.preseedContent')}</label>
            {loading
              ? <div className='text-center py-8 text-gray-600 text-sm'>{t('common.loading')}</div>
              : (
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  rows={20}
                  spellCheck={false}
                  className='w-full bg-gray-950 border border-gray-700 rounded-xl px-4 py-3 text-xs font-mono text-gray-300 leading-relaxed resize-y focus:outline-none focus:border-blue-500'
                />
              )}
          </div>
        </div>

        <div className='p-5 border-t border-gray-800 flex justify-end gap-2'>
          <button
            onClick={onClose}
            className='px-4 py-2 rounded-xl text-sm text-gray-400 border border-gray-700 hover:bg-gray-800'
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={() => saveMut.mutate()}
            disabled={!name || !label || !content || saveMut.isPending}
            className='flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-40'
          >
            {saveMut.isPending
              ? <Loader2 size={14} className='animate-spin' />
              : <CheckCircle2 size={14} />}
            {t('common.save')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Preseeds Tab ─────────────────────────────────────────────────────────────

function PreseedsTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Preseed | null | 'new'>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['netboot-preseeds'],
    queryFn: () => api.mcp<{ ok: boolean; preseeds: Preseed[] }>('netboot.preseeds', { action: 'list' }),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.mcp('netboot.preseeds', { action: 'delete', id }),
    onSuccess: () => {
      toast.success(t('netboot.preseedDeleted'));
      qc.invalidateQueries({ queryKey: ['netboot-preseeds'] });
    },
  });

  const preseeds = data?.preseeds ?? [];

  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-between'>
        <p className='text-xs text-gray-500'>{t('netboot.preseedCount', { count: preseeds.length })}</p>
        <button
          onClick={() => setEditing('new')}
          className='flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium'
        >
          <Plus size={14} /> {t('netboot.newPreseed')}
        </button>
      </div>

      {editing && (
        <PreseedEditor
          preseed={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => qc.invalidateQueries({ queryKey: ['netboot-preseeds'] })}
        />
      )}

      {isLoading
        ? <div className='text-center py-8 text-gray-600 text-sm'>{t('common.loading')}</div>
        : preseeds.length === 0
        ? (
          <div className='text-center py-16 text-gray-500'>
            <FileText size={36} className='mx-auto mb-3 text-gray-700' />
            <p>{t('netboot.noPreseeds')}</p>
          </div>
        )
        : (
          <div className='space-y-2'>
            {preseeds.map((p) => (
              <Card key={p.id}>
                <CardContent className='py-3'>
                  <div className='flex items-center gap-3'>
                    <FileText size={16} className='text-blue-400 flex-shrink-0' />
                    <div className='flex-1 cursor-pointer' onClick={() => setEditing(p)}>
                      <div className='font-medium text-white text-sm hover:text-blue-400'>
                        {p.label}
                      </div>
                      <div className='text-[10px] text-gray-500 font-mono'>
                        {p.name} · {p.distro} ·{' '}
                        {t('netboot.variablesCount', { count: Object.keys(p.variables).length })}
                      </div>
                    </div>
                    <div className='flex items-center gap-1'>
                      <button
                        onClick={() => setEditing(p)}
                        className='p-1.5 rounded-lg text-gray-600 hover:text-blue-400 hover:bg-gray-800'
                      >
                        <Eye size={13} />
                      </button>
                      <button
                        onClick={async () => {
                          if (await themedConfirm(t('netboot.confirmDeletePreseed'))) deleteMut.mutate(p.id);
                        }}
                        className='p-1.5 rounded-lg text-gray-600 hover:text-red-400 hover:bg-gray-800'
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
    </div>
  );
}

// ─── PXE Config Tab ───────────────────────────────────────────────────────────

function PxeConfigTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const { data: ifaceData, isLoading: ifaceLoading } = useQuery({
    queryKey: ['netboot-interfaces'],
    queryFn: () =>
      api.mcp<{ ok: boolean; interfaces: NetworkInterface[] }>(
        'netboot_config',
        { action: 'interfaces' },
        { silent: true },
      ),
  });

  const { data: cfgData, isLoading: cfgLoading } = useQuery({
    queryKey: ['netboot-config'],
    queryFn: () =>
      api.mcp<{ ok: boolean; config: NetbootConfig }>(
        'netboot_config',
        { action: 'get' },
        { silent: true },
      ),
  });

  const [selected, setSelected] = useState<string[]>([]);
  const [serverIp, setServerIp] = useState('');
  const [dirty, setDirty] = useState(false);

  // Sync lokálního stavu s načtenou konfigurací
  const cfgLoaded = cfgData?.config;
  const prevCfg = useRef<NetbootConfig | null>(null);
  if (cfgLoaded && cfgLoaded !== prevCfg.current) {
    prevCfg.current = cfgLoaded;
    setSelected(cfgLoaded.interfaces);
    setServerIp(cfgLoaded.server_ip);
    setDirty(false);
  }

  const saveMut = useMutation({
    mutationFn: () =>
      api.mcp('netboot_config', {
        action: 'set',
        interfaces: selected,
        server_ip: serverIp,
      }),
    onSuccess: () => {
      toast.success(t('netboot.configSaved'));
      setDirty(false);
      qc.invalidateQueries({ queryKey: ['netboot-config'] });
      qc.invalidateQueries({ queryKey: ['netboot-status'] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const toggleIface = (name: string) => {
    setSelected((prev) => prev.includes(name) ? prev.filter((i) => i !== name) : [...prev, name]);
    setDirty(true);
  };

  const autodetectIp = () => {
    // Vezme první IP z prvního vybraného rozhraní
    const iface = ifaceData?.interfaces.find((i) => selected.includes(i.name));
    const addr = iface?.addresses[0]?.split('/')[0];
    if (addr) {
      setServerIp(addr);
      setDirty(true);
    } else toast.error(t('netboot.selectInterfaceFirst'));
  };

  const isLoading = ifaceLoading || cfgLoading;

  return (
    <div className='space-y-5'>
      {/* Výběr rozhraní */}
      <Card>
        <CardHeader>
          <CardTitle icon={Server}>{t('netboot.networkInterfaces')}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className='text-xs text-gray-500 mb-4'>
            {t('netboot.interfacesHint')}
          </p>
          {isLoading
            ? <div className='text-center py-6 text-gray-600 text-sm'>{t('common.loading')}</div>
            : !ifaceData?.interfaces.length
            ? (
              <div className='text-center py-6 text-gray-500 text-sm'>
                {t('netboot.noInterfacesFound')}
              </div>
            )
            : (
              <div className='space-y-2'>
                {ifaceData.interfaces.map((iface) => {
                  const isSelected = selected.includes(iface.name);
                  return (
                    <label
                      key={iface.name}
                      className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                        isSelected
                          ? 'border-blue-500/40 bg-blue-500/10'
                          : 'border-gray-800 bg-gray-900/20 hover:bg-gray-800/30'
                      }`}
                    >
                      <input
                        type='checkbox'
                        checked={isSelected}
                        onChange={() => toggleIface(iface.name)}
                        className='accent-blue-500 w-4 h-4'
                      />
                      <div className='flex-1 min-w-0'>
                        <div className='flex items-center gap-2'>
                          <span className='font-mono text-sm font-medium text-white'>
                            {iface.name}
                          </span>
                          <span
                            className={`text-[10px] px-1.5 py-0.5 rounded-md border ${
                              iface.up
                                ? 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20'
                                : 'text-gray-500 bg-gray-800 border-gray-700'
                            }`}
                          >
                            {iface.up ? 'UP' : 'DOWN'}
                          </span>
                        </div>
                        {iface.addresses.length > 0
                          ? (
                            <div className='text-[11px] text-gray-400 font-mono mt-0.5'>
                              {iface.addresses.join('  ·  ')}
                            </div>
                          )
                          : (
                            <div className='text-[11px] text-gray-600 mt-0.5'>
                              {t('netboot.noIpAddress')}
                            </div>
                          )}
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
        </CardContent>
      </Card>

      {/* IP serveru */}
      <Card>
        <CardHeader>
          <CardTitle icon={Settings}>{t('netboot.serverIpTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className='text-xs text-gray-500 mb-3'>
            {t('netboot.serverIpHint')}
          </p>
          <div className='flex gap-2'>
            <input
              type='text'
              value={serverIp}
              onChange={(e) => {
                setServerIp(e.target.value);
                setDirty(true);
              }}
              placeholder={t('netboot.serverIpPlaceholder')}
              className='flex-1 bg-gray-900/60 border border-gray-700 rounded-xl px-3 py-2 text-sm font-mono text-white placeholder-gray-600 focus:outline-none focus:border-blue-500'
            />
            <button
              onClick={autodetectIp}
              className='px-3 py-2 rounded-xl text-xs border border-gray-700 text-gray-400 hover:text-white hover:bg-gray-800 transition-colors'
              title={t('netboot.autodetectTitle')}
            >
              {t('netboot.autodetect')}
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Uložit */}
      <div className='flex justify-end'>
        <button
          onClick={() => saveMut.mutate()}
          disabled={!dirty || saveMut.isPending}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
            dirty
              ? 'bg-blue-600 hover:bg-blue-500 text-white'
              : 'bg-gray-800 text-gray-600 cursor-not-allowed'
          }`}
        >
          {saveMut.isPending
            ? <Loader2 size={14} className='animate-spin' />
            : <CheckCircle2 size={14} />}
          {t('netboot.saveConfig')}
        </button>
      </div>

      {/* Info o aktuálně aktivní konfiguraci */}
      {cfgData?.config && (
        <Card>
          <CardHeader>
            <CardTitle icon={Shield}>{t('netboot.activeConfig')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className='space-y-2 text-sm'>
              <div className='flex items-start gap-2'>
                <span className='text-gray-500 w-36 shrink-0'>{t('netboot.interfacesRow')}</span>
                <span className='font-mono text-gray-300 text-xs'>
                  {cfgData.config.interfaces.length
                    ? cfgData.config.interfaces.join(', ')
                    : <span className='text-amber-400'>{t('netboot.notSelected')}</span>}
                </span>
              </div>
              <div className='flex items-center gap-2'>
                <span className='text-gray-500 w-36 shrink-0'>{t('netboot.serverIpRow')}</span>
                <span className='font-mono text-gray-300 text-xs'>
                  {cfgData.config.server_ip || (
                    <span className='text-amber-400'>{t('netboot.notSet')}</span>
                  )}
                </span>
              </div>
              <div className='flex items-center gap-2'>
                <span className='text-gray-500 w-36 shrink-0'>{t('netboot.containerRow')}</span>
                <code className='text-gray-300 font-mono text-xs'>
                  {cfgData?.config ? 'acm-netboot-dnsmasq' : '—'}
                </code>
              </div>
              <div className='flex items-center gap-2'>
                <span className='text-gray-500 w-36 shrink-0'>{t('netboot.ipxeMenuRow')}</span>
                <code className='text-gray-300 font-mono text-xs'>/apiext/netboot/boot.ipxe</code>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function DeployPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const { data: status } = useQuery<NetbootStatus>({
    queryKey: ['netboot-status'],
    queryFn: () => api.mcp<NetbootStatus>('netboot_status', {}, { silent: true }),
    staleTime: 15_000,
    refetchInterval: 30_000,
  });

  const dnsmasqMut = useMutation({
    mutationFn: (action: 'start' | 'stop') =>
      api.mcp('netboot_status', { action }),
    onSuccess: () => {
      toast.success(t('netboot.ok'));
      qc.invalidateQueries({ queryKey: ['netboot-status'] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const dnsmasqRunning = status?.dnsmasq === 'running';

  return (
    <div className='space-y-6'>
      <PageHeader
        title={t('deploy.pageTitle', 'Deploy')}
        description={t(
          'deploy.pageDescription',
          'Nasazení: PXE/iPXE, ISO, preseed, ACM Live a průvodce instalace',
        )}
        icon={HardDriveDownload}
        actions={
          <div className='flex items-center gap-2'>
            <button
              onClick={() => dnsmasqMut.mutate(dnsmasqRunning ? 'stop' : 'start')}
              className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium border transition-colors ${
                dnsmasqRunning
                  ? 'bg-red-900/20 border-red-800 text-red-400 hover:bg-red-900/30'
                  : 'bg-emerald-900/20 border-emerald-800 text-emerald-400 hover:bg-emerald-900/30'
              }`}
            >
              <Power size={14} />
              {dnsmasqRunning ? t('netboot.stopPxe') : t('netboot.startPxe')}
            </button>
            <button
              onClick={() => qc.invalidateQueries({ queryKey: ['netboot-status'] })}
              className='p-2 rounded-xl text-gray-500 hover:text-white hover:bg-gray-800 border border-gray-800'
            >
              <RefreshCw size={14} />
            </button>
          </div>
        }
      />

      <StatsGrid columns={4}>
        <StatCard
          label={t('netboot.statPxeServer')}
          value={dnsmasqRunning ? t('netboot.statRunning') : t('netboot.statStopped')}
          icon={Server}
          variant={dnsmasqRunning ? 'success' : 'default'}
        />
        <StatCard label='ISO' value={status?.isos ?? 0} icon={Disc} />
        <StatCard label='Preseed' value={status?.preseeds ?? 0} icon={FileText} />
        <StatCard
          label={t('netboot.builds')}
          value={`${status?.builds_ready ?? 0}/${status?.builds ?? 0}`}
          icon={Wrench}
          variant={(status?.builds_ready ?? 0) > 0 ? 'success' : 'default'}
        />
      </StatsGrid>

      {/* Quick Links — DHCP config, URLs */}
      {status?.server_ip && (
        <Card>
          <CardContent className='py-3'>
            <div className='flex items-start gap-6 flex-wrap text-xs'>
              <div>
                <span className='text-gray-500 block mb-1'>{t('netboot.dhcpOptions')}</span>
                <div className='font-mono text-gray-300 space-y-0.5'>
                  <div>
                    Option 66: <span className='text-white'>{status.server_ip}</span>
                  </div>
                  <div>
                    Option 67: <span className='text-white'>ipxe/undionly.kpxe</span>
                  </div>
                </div>
              </div>
              <div>
                <span className='text-gray-500 block mb-1'>{t('netboot.endpoints')}</span>
                <div className='space-y-0.5'>
                  {[
                    { label: 'iPXE menu', path: '/apiext/netboot/boot.ipxe' },
                    { label: 'Bootstrap', path: '/apiext/netboot/bootstrap.sh' },
                    { label: 'Preseed', path: '/apiext/netboot/preseeds/acm-os.cfg' },
                  ].map((ep) => (
                    <div key={ep.path} className='flex items-center gap-2'>
                      <span className='text-gray-400'>{ep.label}:</span>
                      <code className='text-blue-400 font-mono text-[10px]'>
                        https://{status.server_ip}:{status.api_port ?? DEFAULT_API_TLS_PORT}{ep.path}
                      </code>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(
                            `https://${status.server_ip}:${status.api_port ?? DEFAULT_API_TLS_PORT}${ep.path}`,
                          );
                          toast.success(t('netboot.copied'));
                        }}
                        className='text-gray-600 hover:text-white'
                      >
                        <Copy size={10} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultTab='live'>
        <div className='space-y-5'>
          <TabList>
            <Tab id='live' icon={<HardDriveDownload size={14} />}>
              {t('deploy.tabLive', 'Live / průvodce')}
            </Tab>
            <Tab id='builds' icon={<Wrench size={14} />}>{t('netboot.builds')}</Tab>
            <Tab id='iso' icon={<Disc size={14} />}>ISO</Tab>
            <Tab id='preseeds' icon={<FileText size={14} />}>Preseed</Tab>
            <Tab id='settings' icon={<Settings size={14} />}>{t('netboot.tabPxeConfig')}</Tab>
          </TabList>

          <TabPanel id='live'>
            <LiveDeployWizard />
          </TabPanel>
          <TabPanel id='builds'>
            <BuildsTab />
          </TabPanel>
          <TabPanel id='iso'>
            <IsoTab />
          </TabPanel>
          <TabPanel id='preseeds'>
            <PreseedsTab />
          </TabPanel>
          <TabPanel id='settings'>
            <PxeConfigTab />
          </TabPanel>
        </div>
      </Tabs>
    </div>
  );
}
